import { Inject, NgModule } from '@angular/core';
import { CoreModule } from '@core/core.module';
import { ROUTER_CANCEL, ROUTER_ERROR, ROUTER_NAVIGATION, ROUTER_NAVIGATION_CANCEL } from '@core/state/action-types';
import { IAppState } from '@core/state/app/app-state';
import { RouterNavigationCancelAction, RouterNavigationSuccessAction, RouterToChildNavigation, SetRouterStateAction } from '@core/state/router/router-actions';
import { getRouterState } from '@core/state/router/router-selectors';
import { IAppRouterState } from '@core/state/router/router-state';
import { ActionsSubject, Store } from '@ngrx/store';
import {
    StateDeclaration,
    StateObject,
    Transition,
    UIRouter,
    UIRouterModule
} from '@uirouter/angular';
import { filter } from 'rxjs';

/**
 * Connects RouterModule with StoreModule.
 *
 * During the navigation, before any guards or resolvers run, the router will dispatch
 * a ROUTER_NAVIGATION action, which has the following signature:
 *
 * ```
 * export type RouterNavigationPayload = {
 *   routerState: AppRouterState,
 *   event: RoutesRecognized
 * }
 * ```
 *
 * Either a reducer or an effect can be invoked in response to this action.
 * If the invoked reducer throws, the navigation will be canceled.
 *
 * If navigation gets canceled because of a guard, a ROUTER_CANCEL action will be
 * dispatched. If navigation results in an error, a ROUTER_ERROR action will be dispatched.
 *
 * Both ROUTER_CANCEL and ROUTER_ERROR contain the store$ state before the navigation
 * which can be used to restore$ the consistency of the store$.
 *
 * Usage:
 *
 * ```typescript
 * \@NgModule({
 *   declarations: [AppCmp, SimpleCmp],
 *   imports: [
 *     BrowserModule,
 *     StoreModule.forRoot(mapOfReducers),
 *     RouterModule.forRoot([
 *       { path: '', component: SimpleCmp },
 *       { path: 'next', component: SimpleCmp }
 *     ]),
 *     AppRouterStoreModule
 *   ],
 *   bootstrap: [AppCmp]
 * })
 * export class AppModule {
 * }
 * ```
 */
@NgModule({
    imports: [CoreModule.forChild(), UIRouterModule],
    exports: [],
    declarations: [],
    providers: [],
})
export class AppRouterStoreModule {
    private routerState: any;
    private dispatchTriggeredByRouter: boolean;
    private navigationTriggeredByDispatch: boolean;
    private lastRoutesRecognized: StateDeclaration;
    private lastParams: any;
    private listenTo: string[] = [ROUTER_NAVIGATION_CANCEL];
    private cancelNavigation: string;
    private cancelNavigationOptions: any;

    constructor(
        @Inject(Store) private store$: Store<IAppState>,
        @Inject(UIRouter) private router: UIRouter,
        private actionsSubject$: ActionsSubject
    ) {
        this.dispatchTriggeredByRouter = false;
        this.navigationTriggeredByDispatch = false;
        this.initRouterHooks();
        this.initData();
        this.setUpStateRollbackEvents();
    }

    /**
     * Hook into the router change events.
     */
    initRouterHooks() {
        this.actionsSubject$
            .pipe(filter((action) => this.listenTo.indexOf(action.type) >= 0))
            .subscribe((action: RouterNavigationCancelAction) =>
                this.shouldCancelNavigation(
                    action.payload.state,
                    action.payload.options
                )
            );

        this.router.transitionService.onEnter({}, (trans: Transition): any => {
            // TODO: Rename action to ROUTER_ENTER
            // TODO: Cleanup this hack to stop route events per route level, extend Actions to support filter at action level
            if (!trans['isEntered']) {
                trans['isEntered'] = true;

                // Store state and dispatch
                this.lastRoutesRecognized = this.router.globals.current;
                this.lastParams = { ...this.router.globals.params };

                // Build target url
                const targetState: StateObject = trans.$to();
                let targetURL: string = this.router.stateService.href(
                    targetState,
                    { ...trans.params('to') },
                    { inherit: true }
                );
                if (targetURL?.charAt(0) === '#') {
                    targetURL = targetURL.slice(1, targetURL.length);
                }

                this.routerState = {
                    url: targetURL,
                    name: targetState.name,
                    params: trans.params('to'),
                    fromState: {
                        url: this.lastRoutesRecognized.url,
                        name: this.lastRoutesRecognized.name,
                        params: this.lastParams,
                    },
                } as IAppRouterState;

                if (this.shouldDispatchRouterNavigation()) {
                    this.dispatchRouterNavigation();
                }

                if (
                    this.cancelNavigation &&
                    this.cancelNavigation.length &&
                    trans.to().name === this.cancelNavigation
                ) {
                    this.cancelNavigation = null;
                    this.cancelNavigationOptions = null;
                    trans.abort();
                }
            }
        });

        // this is used to explicitly set the the router state on the store when navigating from child to parent
        // since the `onEnter` for parent is not triggered when moving from child to parent unless reload: true is passed
        this.router.transitionService.onExit({}, (trans: Transition) => {
            const targetState = trans.$to();
            const current = this.router.globals.current;
            const options = trans.options();

            // only if navigating from child to parent and the child is not explicitly setting reload to true
            if (
                current.name.indexOf(targetState.name) === 0 &&
                !options.reload
            ) {
                let targetURL: string = this.router.stateService.href(
                    targetState.name,
                    { ...trans.params('to') },
                    { inherit: true }
                );
                if (targetURL.charAt(0) === '#') {
                    targetURL = targetURL.slice(1, targetURL.length);
                }

                this.routerState = {
                    url: targetURL,
                    name: targetState.name,
                    params: trans.params('to'),
                    fromState: {
                        url: current.url,
                        name: current.name,
                        params: trans.params('from'),
                    },
                } as IAppRouterState;

                this.store$.dispatch(
                    new SetRouterStateAction({
                        routerState: this.routerState,
                    })
                );

                // dispatched when navigating from child to parent and reload: true isn't set
                // cos ROUTER_NAVIGATION isn't dispatched, cos onEnter never triggers
                this.store$.dispatch(
                    new RouterToChildNavigation({
                        routerState: this.routerState,
                    })
                );
            }
        });

        this.router.transitionService.onSuccess({}, (trans: Transition) => {
            // not dispatching the router state as payload and reading it inside effect using `withLatestFrom` to ensure
            // latest state is available set from `onEnter` OR `onExit` hooks
            this.store$.dispatch(new RouterNavigationSuccessAction());
        });
    }

    /**
     * Listen for store state change.
     */
    initData() {
        this.store$.select(getRouterState).subscribe((s) => {
            this.routerState = s;
            this.navigateIfNeeded();
        });
    }

    /**
     * @return {?}
     */
    shouldDispatchRouterNavigation() {
        if (!this.routerState) {
            return true;
        }

        return !this.navigationTriggeredByDispatch;
    }

    /**
     * @return {?}
     */
    navigateIfNeeded() {
        if (!this.routerState || !this.routerState.state) {
            return;
        }

        if (!this.dispatchTriggeredByRouter) {
            if (this.router.urlService.url() !== this.routerState.state.url) {
                this.navigationTriggeredByDispatch = true;
                this.router.stateService.go(this.routerState.state.name);
            }
        }
    }

    /**
     * @return {?}
     */
    setUpStateRollbackEvents() {
        // this.router.events.subscribe(e => {
        //     if (e instanceof RoutesRecognized) {
        //         this.lastRoutesRecognized = e;
        //     }
        //     else if (e instanceof NavigationCancel) {
        //         this.dispatchRouterCancel(e);
        //     }
        //     else if (e instanceof NavigationError) {
        //         this.dispatchRouterError(e);
        //     }
        // });
    }

    /**
     * @return {?}
     */
    dispatchRouterNavigation() {
        this.dispatchRouterAction(ROUTER_NAVIGATION, {
            routerState: this.routerState,
        });

        // event: /** @type {?} */ ({
        //     name: this.lastRoutesRecognized.name,
        //     url: this.lastRoutesRecognized.url,
        //     urlAfterRedirects: this.lastRoutesRecognized.redirectTo
        // })
    }

    /**
     * @param {?} event
     * @return {?}
     */
    dispatchRouterCancel(event) {
        this.dispatchRouterAction(ROUTER_CANCEL, {
            routerState: this.routerState,
            event,
        });
    }

    /**
     * @param {?} event
     * @return {?}
     */
    dispatchRouterError(event) {
        this.dispatchRouterAction(ROUTER_ERROR, {
            routerState: this.routerState,
            event,
        });
    }

    /**
     * @param {?} type
     * @param {?} payload
     * @return {?}
     */
    dispatchRouterAction(type, payload) {
        this.dispatchTriggeredByRouter = true;
        try {
            this.store$.dispatch({ type, payload });
        } finally {
            this.dispatchTriggeredByRouter = false;
            this.navigationTriggeredByDispatch = false;
        }
    }

    shouldCancelNavigation(state: string, options?: any) {
        this.cancelNavigation = state;
        this.cancelNavigationOptions = options;
    }
}